import { FC } from 'react';
import clsx from 'clsx';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import makeStyles from '@mui/styles/makeStyles';
import { Box, FormHelperText, FormLabel, Theme } from '@mui/material';
import { isWysiwygEmpty } from '../../helpers';

interface IWysiwygProps {
  className?: string;
  id: string;
  onBlur?: () => void;
  onChange: (html: string) => void;
  onFocus?: () => void;
  readOnly?: boolean;
  value: string;
  editorFormats?: Array<string>;
  editorOptions?: Array<any>;
  placeholder?: string;
  label?: string;
  isRequired?: boolean;
  isError?: boolean;
  errorMessage?: string;
}

export const Wysiwyg: FC<IWysiwygProps> = ({
  className,
  id,
  onBlur,
  onChange,
  onFocus,
  readOnly,
  value,
  editorFormats,
  editorOptions,
  placeholder,
  label,
  isRequired = false,
  isError = false,
  errorMessage = 'Required'
}) => {
  const classes = useStyles({ isError });

  const options = editorOptions ? editorOptions : undefined;
  //For anyone wanting to customize this: https://quilljs.com/docs/formats/
  const formats = editorFormats ? editorFormats : undefined;

  return (
    <>
      <Box mb={1}>
        {label && (
          <FormLabel
            required={isRequired}
            className={clsx(classes.formLabel, isError && classes.errorLabel)}
          >
            {label}
          </FormLabel>
        )}
        <ReactQuill
          className={clsx(classes.wysiwyg, className ? className : '', isError && classes.error)}
          data-testid={id}
          formats={formats}
          id={id}
          modules={{
            clipboard: { matchVisual: false },
            toolbar: options
          }}
          onBlur={onBlur}
          onChange={newValue => {
            if (newValue !== value) {
              if (!isWysiwygEmpty(newValue)) {
                onChange(newValue);
              } else {
                onChange('');
              }
            }
          }}
          onFocus={() => {
            onFocus && onFocus();
          }}
          readOnly={readOnly}
          value={value}
          defaultValue={''}
          theme="snow"
          placeholder={placeholder}
        />
      </Box>
      {isError && (
        <Box mt={0.5}>
          <FormHelperText error={isError}>{errorMessage}</FormHelperText>
        </Box>
      )}
    </>
  );
};

const useStyles = makeStyles<Theme, { isError: boolean }>(theme => ({
  formLabel: { marginBottom: theme.spacing(0.5), display: 'block' },
  wysiwyg: {
    maxHeight: '150px',
    height: '150px',
    paddingBottom: theme.spacing(1.5),

    '& p': {
      fontFamily: theme.typography.body1.fontFamily,
      fontSize: theme.typography.body1.fontSize
    }
  },
  errorLabel: {
    color: theme.palette.error.main
  },
  error: {
    '& .ql-toolbar': {
      borderBottom: `1px solid ${theme.palette.error.main}`
    },
    '& .ql-container': {
      borderLeft: `1px solid ${theme.palette.error.main}`,
      borderRight: `1px solid ${theme.palette.error.main}`,
      borderBottom: `1px solid ${theme.palette.error.main}`
    }
  }
}));
